import { ChangeDetectionStrategy, Component, effect, input, output, ViewEncapsulation } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { Bind } from "../../decorators/bind.decorator";
import { S25Util } from "../../util/s25-util";
import { SwarmSchedule } from "../s25-swarm-schedule/SwarmSchedule";
import { BoardService } from "../s25-swarm-schedule/s25.board.service";
import { S25RadioModule } from "../s25-radio/s25.radio.module";
import { s25Resource } from "../../util/s25.resource";
import { LynxService } from "../../services/lynx.service";

@Component({
    standalone: true,
    selector: "s25-ng-edit-mpg",
    template: `
        <form [formGroup]="form" (submit)="onSave()">
            <label for="createAcademicGridName">Name:</label>
            <div class="input-wrapper">
                <input id="createAcademicGridName" [formControlName]="'name'" class="c-input" />
                @if (name.invalid && (name.dirty || name.touched)) {
                    @if (name.hasError("required")) {
                        <span class="error-message">Enter a name</span>
                    }
                }
            </div>

            <label>Event Search: </label>
            <span>{{ grid().eventQueryName }}</span>

            <label>Location Search: </label>
            <span>{{ grid().locationQueryName }}</span>

            @if (pushbackEnabled.value()) {
                <label>Editing: </label>
                <div>
                    <s25-ng-radio [name]="'pushback'" [formControlName]="'pushback'" [radioValue]="false">
                        Location Assignments Only
                    </s25-ng-radio>
                    <s25-ng-radio [name]="'pushback'" [formControlName]="'pushback'" [radioValue]="true">
                        Location Assignments and Meeting Patterns
                    </s25-ng-radio>
                </div>
            }
        </form>

        <div class="footer">
            <s25-ng-button [type]="'primary'" [onClick]="onSave">Save</s25-ng-button>
            <s25-ng-button [type]="'danger--outline'" (clicked)="done.emit()">Cancel</s25-ng-button>
        </div>
    `,
    styles: `
        :host {
            display: grid;
            align-content: space-between;
            min-height: 25rem; /* for the search dropdown... */
        }

        form {
            display: grid;
            grid-template-columns: auto 1fr;
            grid-column-gap: 1rem;
            grid-row-gap: 0.5rem;
        }

        label {
            align-content: center;
            font-weight: bold;
        }

        .input-wrapper {
            display: grid;
            max-width: 20em;
        }

        .error-message {
            color: var(--error-message-color);
            font-size: 0.8em;
        }

        .footer {
            display: flex;
            justify-content: right;
            gap: 0.5rem;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, S25ButtonComponent, S25DropdownPaginatedModule, S25RadioModule],
})
export class S25MeetingPatternGridEditComponent {
    grid = input.required<SwarmSchedule.BoardI>();

    done = output();

    form = new FormGroup({
        name: new FormControl<string>("", Validators.required),
        pushback: new FormControl<boolean>(false),
    });

    readonly pushbackEnabled = s25Resource({ defaultValue: false, loader: LynxService.getExpSecEnabled });

    get name() {
        return this.form.get("name");
    }

    constructor() {
        effect(() => {
            this.form.patchValue({ name: this.grid().boardName, pushback: this.grid().mpgPushback });
        });
    }

    @Bind
    async onSave() {
        if (!this.form.dirty) this.done.emit(); // Save with no changes => cancel

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const formData = S25Util.reactiveForms.getDirtyValues(this.form);
        const [_, err] = await S25Util.Maybe(
            BoardService.update({
                id: this.grid().boardId,
                name: formData.name,
                pushback: formData.pushback,
            }),
        );
        if (!!err) {
            alert("Something went wrong when updating your grid.");
            return;
        }
        this.grid().boardName = this.name.value;
        this.grid().mpgPushback = this.form.get("pushback").value;
        this.done.emit();
    }
}
